import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

/**
 * Which Similar Item Alerts surface the alert was set or removed from. The ended-lot PDP and the
 * Price Results page have shipped; the For You tab and the Emails & Notifications section each add
 * their own member when they ship.
 */
export enum SimilarItemAlertSurface {
    PDP = 'PDP',
    PriceResults = 'Price Results',
}

export type SimilarItemAlertPayload = TrackingPayload & {
    itemId: number;
    surface: SimilarItemAlertSurface;
};

export const trackSimilarItemAlertSetAnalytics = (payload: SimilarItemAlertPayload) => {
    track(TrackingEvents.SimilarItems.SimilarItemAlertSet, payload);
};

export const trackSimilarItemAlertRemovedAnalytics = (payload: SimilarItemAlertPayload) => {
    track(TrackingEvents.SimilarItems.SimilarItemAlertRemoved, payload);
};

type SimilarItemAlertArgs = Omit<SimilarItemAlertPayload, 'pageName' | 'url'>;

const useSimilarItemAlertPayload = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return useCallback(
        ({ itemId, surface }: SimilarItemAlertArgs): SimilarItemAlertPayload => ({
            itemId,
            pageName: getAmplitudePageNameFromPath(pathname),
            surface,
            url,
        }),
        [pathname, url]
    );
};

export const useTrackSimilarItemAlertSet = () => {
    const buildPayload = useSimilarItemAlertPayload();

    return useCallback(
        (args: SimilarItemAlertArgs) => {
            trackSimilarItemAlertSetAnalytics(buildPayload(args));
        },
        [buildPayload]
    );
};

export const useTrackSimilarItemAlertRemoved = () => {
    const buildPayload = useSimilarItemAlertPayload();

    return useCallback(
        (args: SimilarItemAlertArgs) => {
            trackSimilarItemAlertRemovedAnalytics(buildPayload(args));
        },
        [buildPayload]
    );
};

/** The reason a bidder gives for removing an alert. Values must match the Amplitude event spec. */
export enum SimilarItemAlertRemovalReason {
    FoundElsewhere = 'Found Elsewhere',
    FoundOnLA = 'Found on LA',
    NotInterested = 'Not Interested',
    Other = 'Other',
    TooManyAlerts = 'Too Many Alerts',
}

/** How the bidder left the feedback modal. */
export type AlertRemovalFeedbackAction = 'dismissed' | 'skipped' | 'submitted';

const usePageContext = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return { pageName: getAmplitudePageNameFromPath(pathname), url };
};

export const useTrackSimilarItemAlertDeleted = () => {
    const { pageName, url } = usePageContext();

    return useCallback(
        ({ daysSinceAdded, itemId }: { daysSinceAdded: number; itemId: number }) => {
            track(TrackingEvents.SimilarItems.SimilarItemAlertDeleted, { daysSinceAdded, itemId, pageName, url });
        },
        [pageName, url]
    );
};

type AlertRemovalFeedbackArgs = {
    action: AlertRemovalFeedbackAction;
    itemId: number;
    reason: SimilarItemAlertRemovalReason | null;
    // Free text is only collected (and only sent) when the reason is "Other".
    reasonDetail: string | null;
};

export const useTrackAlertRemovalFeedback = () => {
    const { pageName, url } = usePageContext();

    return useCallback(
        ({ action, itemId, reason, reasonDetail }: AlertRemovalFeedbackArgs) => {
            track(TrackingEvents.SimilarItems.AlertRemovalFeedback, {
                action,
                itemId,
                pageName,
                reason,
                reasonDetail,
                url,
            });
        },
        [pageName, url]
    );
};
